import { Box, Typography, styled } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { dayjs } from "@outschool/time";
import { ExternalLink } from "@outschool/ui-components-shared";
import { Input, Subscript } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React, { FormEvent } from "react";

const SUPPORTED_FORMATS = ["YYYY-MM-DD h:mm A", "YYYY-MM-D h:mm A"];
const SUPPORTED_TIME_FORMATS = ["h:mmA", "h:mm A"];

export default function EditableDateTime({
  onChange = () => {},
  help,
  tipsLink,
  label,
  optional,
  ...props
}: {
  label: string;
  help?: string;
  tipsLink?: string;
  optional?: boolean;
} & Omit<DateTimeInputProps, "onError">) {
  const { t } = useTranslation("client\\components\\EditableDateTime");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const handleChange = React.useCallback(
    (dateTime: Date | null) => {
      onChange(dateTime);
      setErrorMessage(null);
    },
    [onChange]
  );

  return (
    <Box flex sx={{ flexWrap: "wrap" }}>
      <Box
        sx={(theme: $TSFixMe) => ({
          width: "100%",
          float: "left",
          textAlign: "left",

          [theme.breakpoints.up("md")]: {
            width: "22%",
            textAlign: "right",
            marginTop: "0.25em"
          }
        })}
      >
        <Typography variant="h4">
          {label}
          {optional ? " " + t("(optional)") : null}
        </Typography>
        {help || tipsLink ? (
          <Subscript>
            {help}
            {tipsLink ? (
              <>
                {" "}
                <ExternalLink url={tipsLink}>{t("See tips")}</ExternalLink>
              </>
            ) : null}
          </Subscript>
        ) : null}
      </Box>
      <Box
        sx={(theme: $TSFixMe) => ({
          width: "100%",
          float: "left",
          [theme.breakpoints.up("md")]: { width: "74%", marginLeft: "4%" }
        })}
      >
        <DateTimeInput
          {...props}
          label={label}
          onChange={handleChange}
          onError={setErrorMessage}
        />
        <br />
        {errorMessage && <Box sx={{ color: "error.main" }}>{errorMessage}</Box>}
      </Box>
    </Box>
  );
}

interface DateTimeInputProps {
  dateTime: dayjs.ConfigType | null;
  timeZone: string;
  onChange: (date: Date | null) => void;
  onError?: (error: string) => void;
  min?: dayjs.ConfigType;
  minError?: string;
  max?: dayjs.ConfigType;
  maxError?: string;
  defaultHour?: number;
  defaultTime?: string | 0;
  showDate?: boolean;
  showTime?: boolean;
  disabled?: boolean;
  label?: string;
}

export function DateTimeInput({
  dateTime,
  timeZone,
  onChange,
  min,
  minError,
  max,
  maxError,
  disabled,
  label,
  defaultHour = 9,
  defaultTime = "12:00 PM",
  showDate = true,
  showTime = false,
  onError = () => {}
}: DateTimeInputProps) {
  const { t } = useTranslation("client\\components\\EditableDateTime");
  const isMobile = useIsMobile();

  const ref = React.useRef<HTMLInputElement | undefined>();
  // this is a state because sometimes it's not correctly formatted yet
  const [date, setDate] = React.useState(
    Time.dateInputString(dateTime, timeZone) ?? ""
  );
  const time = Time.timeInputString(dateTime, timeZone);

  //callbacks
  const saveEdits = React.useCallback(
    (date: string | 0, time: string | 0) => {
      let dateTime: dayjs.Dayjs | null;
      if (!date) {
        // clearing the date clears both date and time
        dateTime = null;
      } else {
        const isTimeValid = dayjs(time, SUPPORTED_TIME_FORMATS).isValid();
        time = (isTimeValid && time) || defaultTime;
        let dateTimeString = `${date} ${time}`;
        const stringFormat = SUPPORTED_FORMATS.find(fmt =>
          dayjs(dateTimeString, fmt).isValid()
        );
        if (stringFormat) {
          dateTime = dayjs(dateTimeString, stringFormat).tz(timeZone, true);
        } else {
          onError(t("Invalid date"));
          return;
        }
      }

      if (dateTime && dateTime.isValid()) {
        // We allow invalid dateTime: that's how the user clears the date.

        let errorMessage: string;
        if (min && dateTime.isBefore(min)) {
          // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
          errorMessage = minError;
        } else if (max && dateTime.isAfter(max)) {
          // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
          errorMessage = maxError;
        }

        // @ts-ignore TS(2454): Variable 'errorMessage' is used before being assig... Remove this comment to see the full error message
        if (errorMessage) {
          onError(errorMessage);
          return;
        }
      }

      onChange(dateTime ? dateTime.toDate() : null);
    },
    [onChange, onError, defaultTime, timeZone, minError, maxError, max, min, t]
  );

  const handleDateChange = React.useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value;
      setDate(value);
      // We don't want month or day to be single digit because our
      // formatting logic uses '0x' once this component gets new props
      // which becomes an UX issue when someone wants to type 1x, 2x, and 3x
      // So, we do a strict check for 2 digit values to do an auto save
      // while typing. In that respect, if the user types a single digit month
      // or day, we don't do an auto save. User needs to jump to another
      // component or press Enter
      if (dayjs(value, "YYYY-MM-DD", true).isValid()) {
        // @ts-ignore TS(2345): Argument of type 'string | 0 | null | undefined' i... Remove this comment to see the full error message
        saveEdits(value, time);
      }
    },
    [saveEdits, time]
  );
  //end callbacks

  // effects
  React.useEffect(() => {
    // when dateTime updates, update date
    // time is always derived so doesn't need the same thing
    if (dateTime) {
      setDate(Time.dateInputString(dateTime, timeZone) ?? "");
    }
  }, [dateTime, timeZone]);

  React.useEffect(() => {
    // We need to set the date value through JS API as this is the only option
    // that works in all browsers. Beware the value React property hack at the bottom, too.
    // If we set the input[date] value field through react, iOS mobile browser is not happy
    // this is and the line below does not work.
    if (date && ref.current) {
      try {
        ref.current.valueAsDate = new Date(date);
      } catch (e) {
        if (e instanceof DOMException) {
        } else {
          throw e;
        }
      }
    }
  });
  // end effects

  // render code
  function renderDate() {
    const minDate = min
      ? dayjs.tz(min, timeZone).format("YYYY-MM-DD")
      : undefined;
    const maxDate = max
      ? dayjs.tz(max, timeZone).format("YYYY-MM-DD")
      : undefined;
    return (
      <Input
        ref={ref}
        type="date"
        aria-label={label ? t("{{label}} date", { label }) : undefined}
        placeholder={t("eg {{exampleDate}}", {
          exampleDate: dayjs().add(14, "days").format("YYYY-MM-DD")
        })}
        onChange={handleDateChange}
        // @ts-ignore TS(2345): Argument of type 'string | 0 | null | undefined' i... Remove this comment to see the full error message
        onBlur={() => saveEdits(date, time)}
        onKeyPress={e => {
          const ENTER = 13;
          if (e.charCode === ENTER) {
            // @ts-ignore TS(2345): Argument of type 'string | 0 | null | undefined' i... Remove this comment to see the full error message
            saveEdits(date, time);
          }
        }}
        value={isMobile ? "" : date}
        min={minDate}
        max={maxDate}
        disabled={disabled}
        sx={{
          border: "2px solid",
          borderColor: "grey.200"
        }}
      />
    );
  }

  function renderTime() {
    const timeObj = dayjs(time, "h:mm A");
    let currentHour: string | number = defaultHour;
    let currentMinute: string | number = 0;
    let isAM: string | boolean = true;
    if (timeObj.isValid()) {
      const twelveHourFormat = timeObj.format("h:m:A");
      [currentHour, currentMinute, isAM] = twelveHourFormat.split(":");
      isAM = isAM === "AM";
    }

    const setTime = (
      hour: string | number,
      minute: string | number,
      isAM: string | boolean
    ) => {
      const time = `${hour}:${format(minute)} ${isAM ? "AM" : "PM"}`;
      saveEdits(date, time);
    };

    /* eslint-disable jsx-a11y/no-onchange */
    return (
      <Typography sx={{ whiteSpace: "no-wrap" }}>
        {showDate && <span>&nbsp; {t("at")} &nbsp;</span>}
        <select
          value={currentHour}
          aria-label={label ? t("{{label}} hour", { label }) : undefined}
          onChange={e => setTime(e.target.value, currentMinute, isAM)}
          disabled={disabled}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(hour => (
            <option key={"hour" + hour} value={hour}>
              {hour}
            </option>
          ))}
        </select>
        &nbsp;:&nbsp;
        <select
          value={currentMinute}
          aria-label={label ? t("{{label}} minute", { label }) : undefined}
          onChange={e => setTime(currentHour, e.target.value, isAM)}
          disabled={disabled}
        >
          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(minute => (
            <option key={"minute" + minute} value={minute}>
              {format(minute)}
            </option>
          ))}
        </select>
        &nbsp;
        <select
          value={isAM ? "AM" : "PM"}
          aria-label={label ? t("{{label}} AM/PM", { label }) : undefined}
          onChange={e =>
            setTime(currentHour, currentMinute, e.target.value === "AM")
          }
          disabled={disabled}
        >
          <option>{t("AM")}</option>
          <option>{t("PM")}</option>
        </select>
      </Typography>
    );
    /* eslint-enable jsx-a11y/no-onchange */

    function format(n: string | number) {
      return typeof n === "number" ? (n < 10 ? `0${n}` : n) : n;
    }
  }

  return (
    <span>
      {showDate && renderDate()}
      {date && showTime && renderTime()}
    </span>
  );
}

export function StyledDateTimeInput({
  dateTime,
  timeZone,
  onChange,
  min,
  minError,
  max,
  maxError,
  disabled,
  label,
  defaultHour = 9,
  defaultTime = "12:00 PM",
  showDate = true,
  showTime = false,
  onError = () => {}
}: DateTimeInputProps) {
  const { t } = useTranslation("client\\components\\EditableDateTime");
  const isMobile = useIsMobile();

  const ref = React.useRef<HTMLInputElement | undefined>();
  // this is a state because sometimes it's not correctly formatted yet
  const [date, setDate] = React.useState(
    Time.dateInputString(dateTime, timeZone) ?? ""
  );
  const time = Time.timeInputString(dateTime, timeZone);

  //callbacks
  const saveEdits = React.useCallback(
    (date: string | 0, time: string | 0) => {
      let dateTime: dayjs.Dayjs | null;
      if (!date) {
        // clearing the date clears both date and time
        dateTime = null;
      } else {
        const isTimeValid = dayjs(time, SUPPORTED_TIME_FORMATS).isValid();
        time = (isTimeValid && time) || defaultTime;
        let dateTimeString = `${date} ${time}`;
        const stringFormat = SUPPORTED_FORMATS.find(fmt =>
          dayjs(dateTimeString, fmt).isValid()
        );
        if (stringFormat) {
          dateTime = dayjs(dateTimeString, stringFormat).tz(timeZone, true);
        } else {
          onError(t("Invalid date"));
          return;
        }
      }

      if (dateTime && dateTime.isValid()) {
        // We allow invalid dateTime: that's how the user clears the date.

        let errorMessage: string;
        if (min && dateTime.isBefore(min)) {
          // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
          errorMessage = minError;
        } else if (max && dateTime.isAfter(max)) {
          // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
          errorMessage = maxError;
        }

        // @ts-ignore TS(2454): Variable 'errorMessage' is used before being assig... Remove this comment to see the full error message
        if (errorMessage) {
          onError(errorMessage);
          return;
        }
      }

      onChange(dateTime ? dateTime.toDate() : null);
    },
    [onChange, onError, defaultTime, timeZone, minError, maxError, max, min, t]
  );

  const handleDateChange = React.useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      const value = (event.target as HTMLInputElement).value;
      setDate(value);
      // We don't want month or day to be single digit because our
      // formatting logic uses '0x' once this component gets new props
      // which becomes an UX issue when someone wants to type 1x, 2x, and 3x
      // So, we do a strict check for 2 digit values to do an auto save
      // while typing. In that respect, if the user types a single digit month
      // or day, we don't do an auto save. User needs to jump to another
      // component or press Enter
      if (dayjs(value, "YYYY-MM-DD", true).isValid()) {
        // @ts-ignore TS(2345): Argument of type 'string | 0 | null | undefined' i... Remove this comment to see the full error message
        saveEdits(value, time);
      }
    },
    [saveEdits, time]
  );
  //end callbacks

  // effects
  React.useEffect(() => {
    // when dateTime updates, update date
    // time is always derived so doesn't need the same thing
    if (dateTime) {
      setDate(Time.dateInputString(dateTime, timeZone) ?? "");
    }
  }, [dateTime, timeZone]);

  React.useEffect(() => {
    // We need to set the date value through JS API as this is the only option
    // that works in all browsers. Beware the value React property hack at the bottom, too.
    // If we set the input[date] value field through react, iOS mobile browser is not happy
    // this is and the line below does not work.
    if (date && ref.current) {
      try {
        ref.current.valueAsDate = new Date(date);
      } catch (e) {
        if (e instanceof DOMException) {
        } else {
          throw e;
        }
      }
    }
  });
  // end effects

  // render code
  function renderDate() {
    const minDate = min
      ? dayjs.tz(min, timeZone).format("YYYY-MM-DD")
      : undefined;
    const maxDate = max
      ? dayjs.tz(max, timeZone).format("YYYY-MM-DD")
      : undefined;
    return (
      <Input
        ref={ref}
        type="date"
        aria-label={label ? t("{{label}} date", { label }) : undefined}
        placeholder={t("eg {{exampleDate}}", {
          exampleDate: dayjs().add(14, "days").format("YYYY-MM-DD")
        })}
        onChange={handleDateChange}
        // @ts-ignore TS(2345): Argument of type 'string | 0 | null | undefined' i... Remove this comment to see the full error message
        onBlur={() => saveEdits(date, time)}
        onKeyPress={e => {
          const ENTER = 13;
          if (e.charCode === ENTER) {
            // @ts-ignore TS(2345): Argument of type 'string | 0 | null | undefined' i... Remove this comment to see the full error message
            saveEdits(date, time);
          }
        }}
        value={isMobile ? "" : date}
        min={minDate}
        max={maxDate}
        disabled={disabled}
        sx={{
          border: "2px solid",
          width: "100%",
          height: "100%",
          borderRadius: "1rem",
          paddingRight: "2.5rem",
          borderColor: "grey.200",
          "&:disabled": {
            backgroundColor: "grey.100"
          }
        }}
      />
    );
  }

  function renderTime() {
    const timeObj = dayjs(time, "h:mm A");
    let currentHour: string | number = defaultHour;
    let currentMinute: string | number = 0;
    let isAM: string | boolean = true;
    if (timeObj.isValid()) {
      const twelveHourFormat = timeObj.format("h:m:A");
      [currentHour, currentMinute, isAM] = twelveHourFormat.split(":");
      isAM = isAM === "AM";
    }

    const setTime = (
      hour: string | number,
      minute: string | number,
      isAM: string | boolean
    ) => {
      const time = `${hour}:${format(minute)} ${isAM ? "AM" : "PM"}`;
      saveEdits(date, time);
    };

    /* eslint-disable jsx-a11y/no-onchange */
    return (
      <Typography sx={{ whiteSpace: "no-wrap" }}>
        {showDate && <span>&nbsp; {t("at")} &nbsp;</span>}
        <EditableDateTimeSelectBase
          value={currentHour}
          aria-label={label ? t("{{label}} hour", { label }) : undefined}
          onChange={e => setTime(e.target.value, currentMinute, isAM)}
          disabled={disabled}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(hour => (
            <option key={"hour" + hour} value={hour}>
              {hour}
            </option>
          ))}
        </EditableDateTimeSelectBase>
        &nbsp;:&nbsp;
        <EditableDateTimeSelectBase
          value={currentMinute}
          aria-label={label ? t("{{label}} minute", { label }) : undefined}
          onChange={e => setTime(currentHour, e.target.value, isAM)}
          disabled={disabled}
        >
          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(minute => (
            <option key={"minute" + minute} value={minute}>
              {format(minute)}
            </option>
          ))}
        </EditableDateTimeSelectBase>
        &nbsp;
        <EditableDateTimeSelectBase
          value={isAM ? "AM" : "PM"}
          aria-label={label ? t("{{label}} AM/PM", { label }) : undefined}
          onChange={e =>
            setTime(currentHour, currentMinute, e.target.value === "AM")
          }
          disabled={disabled}
        >
          <option>{t("AM")}</option>
          <option>{t("PM")}</option>
        </EditableDateTimeSelectBase>
      </Typography>
    );
    /* eslint-enable jsx-a11y/no-onchange */

    function format(n: string | number) {
      return typeof n === "number" ? (n < 10 ? `0${n}` : n) : n;
    }
  }

  return (
    <span>
      {showDate && renderDate()}
      {date && showTime && renderTime()}
    </span>
  );
}

export const EditableDateTimeSelectBase = styled("select")({
  padding: "6px 10px",
  borderRadius: "4px",
  boxShadow: "none",
  fontSize: "1.6rem",
  fontStyle: "initial",
  "&:focus": {
    outline: "none",
    borderColor: "#4b01d4"
  },
  "&&::placeholder": { color: "grayText", opacity: 1 },
  height: "38px",
  border: "1px solid #EBEBEB",
  webkitAutofill: {
    webkitBoxShadow: "0 0 0px 1000px #f1f1f1 inset !important"
  },
  webkitAppearance: "none",
  mozAppearance: "none",
  appearance: "none"
});
